import { useMemo } from "react";
import {
  useV2ListCatalogCorrelationsQuery,
  useV2ListProjectServicesQuery,
} from "@generated";
import useFlags from "@common/hooks/useFlags";
import useListStandards, { Standard } from "./useListStandards";
import rankSuggestions from "../common/rankSuggestions";

const SUGGESTION_LIMIT = 5;
const CORRELATION_PAGE_SIZE = 100;

// Catalog services commonly added alongside the selected ones and the ones
// already on the project, minus anything in either set. Candidates are loaded
// through the standards list so retired or other-type services drop out and
// each suggestion carries what step 2 needs to apply it.
const useSuggestedStandards = ({
  projectId,
  serviceType,
  selectedIds,
  resourceRates,
  resourceRatesEnabled,
}: {
  projectId: number;
  serviceType: "professional_services" | "managed_services";
  selectedIds: string[];
  resourceRates: any[];
  resourceRatesEnabled: boolean;
}): { suggestions: Standard[] } => {
  const { releaseServiceCorrelations } = useFlags();
  const enabled = releaseServiceCorrelations === true;
  const hasSelection = selectedIds.length > 0;

  const { data: projectServices } = useV2ListProjectServicesQuery(
    {
      filter: { project: String(projectId), active: true },
      include: ["service"],
      page: { size: 1000 },
    },
    { skip: !enabled || !projectId }
  );

  const projectServiceIds = useMemo(
    () =>
      (projectServices?.data || [])
        .map((ps) => ps.relationships?.service?.data?.id)
        .filter((id) => id != null)
        .map(String),
    [projectServices]
  );

  const anchorIds = useMemo(
    // Array.from, not spread: see rankSuggestions.
    () =>
      Array.from(new Set([...selectedIds.map(String), ...projectServiceIds])),
    [selectedIds, projectServiceIds]
  );

  const { data: correlations } = useV2ListCatalogCorrelationsQuery(
    {
      filter: { service_id: [...anchorIds].sort().join(",") },
      page: { size: CORRELATION_PAGE_SIZE },
      sort: ["-confidence-lb"],
    },
    { skip: !enabled || !hasSelection }
  );

  const rankedIds = useMemo(
    () =>
      rankSuggestions(
        (correlations?.data || []).map((row) => ({
          relatedServiceId: String(row.attributes["related-service-id"]),
          confidenceLb: Number(row.attributes["confidence-lb"]) || 0,
        })),
        anchorIds,
        SUGGESTION_LIMIT
      ),
    [correlations, anchorIds]
  );

  const { standards } = useListStandards({
    filterParams: { service_type: serviceType, ids: rankedIds },
    pageSize: SUGGESTION_LIMIT,
    currentPage: 1,
    sortBy: "",
    sortOrder: "asc",
    resourceRates,
    resourceRatesEnabled,
    skip: !enabled || !hasSelection || !rankedIds.length,
  });

  const suggestions = useMemo(() => {
    if (!enabled || !hasSelection || !rankedIds.length) return [];
    // The services list returns its own order; keep the ranking's.
    return rankedIds
      .map((id) => standards.find((s) => s.id === id))
      .filter((s): s is Standard => !!s);
  }, [enabled, hasSelection, rankedIds, standards]);

  return { suggestions };
};

export default useSuggestedStandards;
